







































































































































import { Component, Vue } from "vue-property-decorator";
import { TaxObligationPaymentPayload } from "@/dto/tax/TaxObligationPaymentPayload";
import TaxObligationService from "@/services/TaxObligationService";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalSelect from "@/components/common/PortalSelect.vue";
import PortalMoneyInput from "@/components/common/PortalMoneyInput.vue";
import { TransactionType, BalanceEffect, PaymentStatus, PaymentMethod } from "@/constants/PaymentConstants";
import SelectOption from "@/components/common/SelectOption";
import { processError } from "@/utils/ComponentUtils";
import { TaxObligationLedgerRecordDTO } from "@/dto/tax/TaxObligationDTO";
import { getTaxObligationLiabilityTypeLabel } from "@/constants/TaxObligationLiabilityType";

@Component({
  components: {
    PortalInput,
    PortalSelect,
    PortalMoneyInput
  }
})
export default class AddTaxPaymentModal extends Vue {
  private taxObligationId: number | null = null;
  private ledger: TaxObligationLedgerRecordDTO[] = [];
  private isPastPayment = false;
  private form: any = {
    paymentDate: new Date().toISOString().substr(0, 10),
    status: PaymentStatus.SETTLED,
    transactionId: null,
    paymentMethod: null,
    paymentAccount: null,
    items: []
  };

  public beforeOpen(event: any): void {
    this.taxObligationId = event.params.taxObligationId;
    this.ledger = event.params.ledger || [];
    this.isPastPayment = !!event.params.isPastPayment;
    this.form = {
      paymentDate: new Date().toISOString().substr(0, 10),
      status: this.isPastPayment ? PaymentStatus.SETTLED : PaymentStatus.DRAFT,
      transactionId: null,
      paymentMethod: null,
      paymentAccount: null,
      items: [
        {
          transactionType: null,
          amountCents: null,
          relatedRecordId: null
        }
      ]
    };
    if (this.$validator) {
      this.$validator.reset();
    }
  }

  public closeModal(): void {
    this.$modal.hide('add-tax-payment-modal');
  }

  public addItem(): void {
    if (this.canAddItem) {
      this.form.items.push({
        transactionType: null,
        amountCents: null,
        relatedRecordId: null
      });
    }
  }

  public removeItem(index: number): void {
    this.form.items.splice(index, 1);
  }

  public onTransactionTypeChange(item: any): void {
    item.relatedRecordId = null;
  }

  get dateLabel(): string {
    if (!this.isPastPayment) {
      return this.$t('tax_calendar.labels.scheduled_date') as string;
    }

    switch (this.form.status) {
      case PaymentStatus.SETTLED:
        return this.$t('tax_calendar.labels.settled_date') as string;
      case PaymentStatus.RETURNED:
        return this.$t('tax_calendar.labels.returned_date') as string;
      default:
        return this.$t('tax_calendar.labels.date') as string;
    }
  }

  get statusOptions(): SelectOption[] {
    return [
      SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(PaymentStatus.SETTLED)).value(PaymentStatus.SETTLED).build(),
      SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(PaymentStatus.RETURNED)).value(PaymentStatus.RETURNED).build(),
    ];
  }

  get paymentMethodOptions(): SelectOption[] {
    return Object.keys(PaymentMethod).map(key =>
      SelectOption.builder().title(this.$t('tax_calendar.payment_methods.' + key) as string).value(key).build()
    );
  }

  get today(): string {
    return new Date().toISOString().split('T')[0];
  }

  get availableObligations(): TaxObligationLedgerRecordDTO[] {
    return this.ledger.filter(r => r.transactionType === TransactionType.LIABILITY || r.transactionType === TransactionType.TAX_BILL_ASSESSMENT);
  }

  get availablePenalties(): TaxObligationLedgerRecordDTO[] {
    return this.ledger.filter(r => r.transactionType === TransactionType.PENALTY_ASSESSMENT);
  }

  public getRelatedRecordOptions(item: any): SelectOption[] {
    const records = item.transactionType === TransactionType.OBLIGATION_PAYMENT 
      ? this.availableObligations 
      : this.availablePenalties;

    return records.map(r => {
      const typeLabel = r.liabilityType ? getTaxObligationLiabilityTypeLabel(r.liabilityType) : this.$stringUtils.enumToFormattedSentence(r.transactionType);
      const amount = this.formatCurrency(r.amountCents / 100);
      return SelectOption.builder()
        .title(`${typeLabel} (${this.$dateUtils.formatDateToAmericanFormat(r.createDate)}) - ${amount}`)
        .value(r.id)
        .build();
    });
  }

  public getItemWarning(item: any): string | null {
    if (!item.relatedRecordId || !item.amountCents) return null;

    const record = this.ledger.find(r => r.id === item.relatedRecordId);
    if (!record) return null;

    // Check if already paid
    // In this simplified logic, we check balance snapshot or just look for subsequent payments.
    // However, the requirement says "warning if already paid by another payment".
    // We can check the balanceDue for this specific record if we had that info, 
    // but DTO only has balanceSnapshot at that time.
    
    // A better way: calculate total payments for this record in ledger
    const paymentsForRecord = this.ledger
      .filter(r => r.relatedRecordId === item.relatedRecordId && r.balanceEffect === BalanceEffect.DECREASE && r.paymentStatus !== 'DECLINED')
      .reduce((sum, r) => sum + r.amountCents, 0);
    
    if (paymentsForRecord >= record.amountCents) {
      return this.$t("tax_calendar.warnings.already_paid") as string;
    }

    if (item.amountCents > (record.amountCents - paymentsForRecord)) {
      return this.$t("tax_calendar.warnings.amount_exceeds_balance") as string;
    }

    return null;
  }

  get totalAmountCents(): number {
    return this.form.items.reduce((sum: number, item: any) => sum + (item.amountCents || 0), 0);
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  get canAddItem(): boolean {
    return true;
  }

  get isValid(): boolean {
    if (this.$validator && this.$validator.errors.items.length > 0) return false;
    const basicValid = !!this.form.paymentDate;
    const pastValid = !this.isPastPayment || (!!this.form.status && !!this.form.transactionId);
    const itemsValid = this.form.items.length > 0 && this.form.items.every((item: any) => !!item.transactionType && (item.amountCents > 0));
    return basicValid && pastValid && itemsValid;
  }

  public getTransactionTypeOptions(currentType: TransactionType): SelectOption[] {
    const allowedTypes = [TransactionType.OBLIGATION_PAYMENT, TransactionType.PENALTY_PAYMENT];

    return allowedTypes
      .map(type => 
        SelectOption.builder().title(this.$stringUtils.enumToFormattedSentence(type)).value(type).build()
      );
  }

  public async submit(): Promise<void> {
    const valid = await this.$validator.validateAll();
    if (!valid || !this.isValid || !this.taxObligationId) return;

    const payload: TaxObligationPaymentPayload = {
      paymentDate: this.form.paymentDate,
      status: this.form.status,
      transactionId: this.isPastPayment ? this.form.transactionId : null,
      paymentMethod: this.isPastPayment ? this.form.paymentMethod : null,
      paymentAccount: this.isPastPayment ? this.form.paymentAccount : null,
      isPastPayment: this.isPastPayment,
      totalAmountCents: this.totalAmountCents,
      items: this.form.items
    };

    this.$loadOn();
    try {
      await TaxObligationService.addPayment(this.taxObligationId, payload);
      this.closeModal();
      this.$emit('added');
    } catch (e) {
      processError(e, this);
    } finally {
      this.$loadOff();
    }
  }
}
